import axios from "../axios";
import { downloadHsePdfBlob, raiseHsePdfRequestError } from "./downloadPdf";
import { fetchHseTiltak, updateHseTiltak } from "./tiltak";
import {
  catalogFromApi,
  categoriesFromApi,
  RISK_CATALOG,
} from "../../components/dashboard/hse/risk/riskCatalog";
import {
  normalizeClimate,
  normalizeRisk,
  normalizeSja,
  riskApiError,
  toClimatePayload,
  toRiskPayload,
  toSjaPayload,
  unwrapList,
  unwrapRecord,
} from "../../components/dashboard/hse/risk/riskUtils";
import { normalizeSjaStepTemplates } from "../../components/dashboard/hse/risk/sjaStepTemplates";

const RISK_PATH = "/hse/risk-assessments";
const CAMPAIGN_PATH = "/hse/risk-campaigns";
const SJA_PATH = "/hse/sja";
const CLIMATE_PATH = "/hse/risk/climate";
const REGISTER_PATH = "/hse/risk/register";
const MATRIX_PATH = "/hse/risk/matrix";
const CATEGORIES_PATH = "/hse/risk-categories";
const SETUP_PATH = "/hse/setup_status";

function listParams(filters = {}) {
  const params = {};
  const search = filters.search || filters.query;
  if (search) params.search = search;
  if (filters.status) params.status = filters.status;
  if (filters.theme) params.theme = filters.theme;
  if (filters.risk_level) params.risk_level = filters.risk_level;
  if (filters.hse_risk_category_id) params.hse_risk_category_id = filters.hse_risk_category_id;
  if (filters.hse_risk_campaign_id) params.hse_risk_campaign_id = filters.hse_risk_campaign_id;
  if (filters.year) params.year = filters.year;
  if (filters.risk_category) params.risk_category = filters.risk_category;
  if (filters.work_type) params.work_type = filters.work_type;
  if (filters.page) params.page = filters.page;
  if (filters.per_page) params.per_page = filters.per_page;
  return params;
}

export function raiseRiskError(error) {
  const message = riskApiError(error);
  const next = new Error(message || "error");
  next.response = error?.response;
  throw next;
}

export async function fetchRiskAssessments(filters = {}) {
  try {
    const response = await axios.get(RISK_PATH, { params: listParams(filters) });
    const { items, meta } = unwrapList(response.data);
    return { data: items.map(normalizeRisk), meta };
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function fetchRiskRegister() {
  try {
    const response = await axios.get(REGISTER_PATH);
    const payload = unwrapRecord(response.data) || {};
    return {
      items: (payload.items || []).map(normalizeRisk),
      climate_risks: (payload.climate_risks || []).map(normalizeClimate),
      sja: (payload.sja || []).map(normalizeSja),
      vernerunder: payload.vernerunder || [],
      counts: payload.counts || {},
      matrix: payload.matrix || null,
    };
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function fetchRiskMatrixConfig() {
  try {
    const response = await axios.get(MATRIX_PATH);
    return unwrapRecord(response.data) || {};
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function fetchRiskAssessment(id) {
  try {
    const response = await axios.get(`${RISK_PATH}/${id}`);
    return normalizeRisk(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function createRiskAssessment(form) {
  try {
    const response = await axios.post(RISK_PATH, { risk_assessment: toRiskPayload(form) });
    return normalizeRisk(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function updateRiskAssessment(id, form) {
  try {
    const response = await axios.put(`${RISK_PATH}/${id}`, { risk_assessment: toRiskPayload(form) });
    return normalizeRisk(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function excludeRiskAssessment(id, reason = "") {
  return updateRiskAssessment(id, {
    skip_assessment: true,
    skip_reason: reason,
    status: "skipped",
  });
}

export async function deleteRiskAssessment(id) {
  try {
    await axios.delete(`${RISK_PATH}/${id}`);
  } catch (error) {
    raiseRiskError(error);
  }
}

function normalizeCampaign(record = {}) {
  const progress = record.progress || {};
  return {
    id: record.id,
    title: record.title || "",
    year: record.year || null,
    kind: record.kind || "overall_assessment",
    status: record.status || "active",
    description: record.description || "",
    department_id: record.department_id || "",
    department: record.department || null,
    progress: {
      assessed: Number(progress.assessed) || 0,
      skipped: Number(progress.skipped) || 0,
      total: Number(progress.total) || 0,
      pending: Number(progress.pending) || 0,
    },
    created_at: record.created_at,
    updated_at: record.updated_at,
    risks: Array.isArray(record.risks) ? record.risks.map(normalizeRisk) : [],
    skipped: Array.isArray(record.skipped) ? record.skipped.map(normalizeRisk) : [],
  };
}

export async function fetchRiskCampaigns(filters = {}) {
  try {
    const params = {};
    if (filters.search || filters.query) params.search = filters.search || filters.query;
    if (filters.year) params.year = filters.year;
    if (filters.status) params.status = filters.status;
    if (filters.department_id) params.department_id = filters.department_id;
    if (filters.page) params.page = filters.page;
    if (filters.per_page) params.per_page = filters.per_page;
    const response = await axios.get(CAMPAIGN_PATH, { params });
    const { items, meta } = unwrapList(response.data);
    return { data: items.map(normalizeCampaign), meta };
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function fetchRiskCampaign(id) {
  try {
    const response = await axios.get(`${CAMPAIGN_PATH}/${id}`);
    return normalizeCampaign(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function createRiskCampaign(form) {
  try {
    const response = await axios.post(CAMPAIGN_PATH, {
      risk_campaign: {
        year: form.year,
        title: form.title || undefined,
        description: form.description || "",
        kind: form.kind || (form.title ? "custom" : "overall_assessment"),
        department_id: form.department_id || undefined,
      },
    });
    return normalizeCampaign(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function updateRiskCampaign(id, form) {
  try {
    const response = await axios.put(`${CAMPAIGN_PATH}/${id}`, {
      risk_campaign: {
        year: form.year,
        title: form.title,
        description: form.description,
        kind: form.kind,
        status: form.status,
        department_id: form.department_id || null,
      },
    });
    return normalizeCampaign(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function deleteRiskCampaign(id) {
  try {
    await axios.delete(`${CAMPAIGN_PATH}/${id}`);
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function suggestRiskCampaignDrafts(campaignId, attrs = {}) {
  try {
    const response = await axios.post(`${CAMPAIGN_PATH}/${campaignId}/suggest`, {
      suggestion: {
        industry: attrs.industry || "",
        fag: attrs.fag || "",
        discipline_id: attrs.discipline_id || undefined,
        context: attrs.context || "",
        count: attrs.count || 5,
      },
    });
    const { items, meta } = unwrapList(response.data);
    return { data: items.map(normalizeRisk), meta: meta || response.data?.meta || {} };
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function fetchRiskCatalog() {
  try {
    const response = await axios.get(`${RISK_PATH}/catalog`);
    const payload = response.data;
    return {
      themes: catalogFromApi(payload),
      categories: categoriesFromApi(payload),
      risk_levels: payload?.data?.risk_levels || [],
      statuses: payload?.data?.statuses || [],
      matrix: payload?.data?.matrix || null,
    };
  } catch (_error) {
    return { themes: RISK_CATALOG, categories: [] };
  }
}

export async function suggestRiskAssessments(attrs = {}) {
  try {
    const response = await axios.post(`${RISK_PATH}/suggest`, {
      suggestion: {
        industry: attrs.industry || "",
        fag: attrs.fag || "",
        discipline_id: attrs.discipline_id || undefined,
        context: attrs.context || "",
        count: attrs.count || 5,
        hse_risk_campaign_id: attrs.hse_risk_campaign_id || attrs.campaign_id,
        year: attrs.year,
      },
    });
    const { items, meta } = unwrapList(response.data);
    return { data: items.map(normalizeRisk), meta: meta || response.data?.meta || {} };
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function fetchRiskCategories() {
  try {
    const response = await axios.get(CATEGORIES_PATH);
    const { items } = unwrapList(response.data);
    return items;
  } catch (_error) {
    return [];
  }
}

export async function fetchHseSetupStatus() {
  try {
    const response = await axios.get(SETUP_PATH);
    return unwrapRecord(response.data) || { ready: true, issues: [], flags: {} };
  } catch (_error) {
    return { ready: true, issues: [], flags: {} };
  }
}

export async function fetchRiskTiltak(riskId) {
  const response = await fetchHseTiltak({
    hse_risk_assessment_id: riskId,
    per_page: 50,
  });
  return response?.data || [];
}

export async function createRiskTiltak(riskId, action) {
  try {
    const response = await axios.post(`${RISK_PATH}/${riskId}/tiltak`, {
      tiltak: {
        title: action.title,
        description: action.description || "",
        action_type: action.action_type || "generic",
        deadline: action.deadline,
        responsible_user_id: action.responsible_user_id,
        priority: action.priority,
        status: action.status,
      },
    });
    return unwrapRecord(response.data);
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function linkRiskTiltak(riskId, tiltakId) {
  return updateHseTiltak(tiltakId, {
    hse_risk_assessment_id: riskId,
  });
}

export async function fetchSjas(filters = {}) {
  try {
    const response = await axios.get(SJA_PATH, { params: listParams(filters) });
    const { items, meta } = unwrapList(response.data);
    return { data: items.map(normalizeSja), meta };
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function fetchSja(id) {
  try {
    const response = await axios.get(`${SJA_PATH}/${id}`);
    return normalizeSja(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function createSja(form) {
  try {
    const response = await axios.post(SJA_PATH, { sja: toSjaPayload(form) });
    return normalizeSja(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function updateSja(id, form) {
  try {
    const response = await axios.put(`${SJA_PATH}/${id}`, { sja: toSjaPayload(form) });
    return normalizeSja(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function submitSja(id) {
  try {
    const response = await axios.post(`${SJA_PATH}/${id}/submit`);
    return normalizeSja(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function approveSja(id, { role } = {}) {
  try {
    const body = role ? { role } : {};
    const response = await axios.post(`${SJA_PATH}/${id}/approve`, body);
    return normalizeSja(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function fetchSjaStepTemplates() {
  try {
    const response = await axios.get(`${SJA_PATH}/step-templates`);
    return normalizeSjaStepTemplates(response.data, "api");
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function applySjaStepTemplateToSja(id, stepTemplateId) {
  try {
    const response = await axios.post(`${SJA_PATH}/${id}/apply_template`, {
      step_template_id: stepTemplateId,
    });
    return normalizeSja(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function exportRiskAssessmentPdf(id, filename) {
  try {
    const response = await axios.get(`${RISK_PATH}/${id}/export_pdf`, {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    await downloadHsePdfBlob(response.data, filename || `hse-risiko-${id}.pdf`);
  } catch (error) {
    await raiseHsePdfRequestError(error);
  }
}

export async function exportRiskRegisterPdf(filters = {}, filename) {
  try {
    const response = await axios.get(`${RISK_PATH}/export_pdf`, {
      params: listParams(filters),
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    await downloadHsePdfBlob(response.data, filename || "hse-risikoregister.pdf");
  } catch (error) {
    await raiseHsePdfRequestError(error);
  }
}

export async function exportSjaPdf(id, filename) {
  try {
    const response = await axios.get(`${SJA_PATH}/${id}/export_pdf`, {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    await downloadHsePdfBlob(response.data, filename || `hse-sja-${id}.pdf`);
  } catch (error) {
    await raiseHsePdfRequestError(error);
  }
}

export async function fetchClimateRisk(id) {
  try {
    const response = await axios.get(`${CLIMATE_PATH}/${id}`);
    return normalizeClimate(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function updateClimateRisk(id, form) {
  try {
    const response = await axios.put(`${CLIMATE_PATH}/${id}`, { climate_risk: toClimatePayload(form) });
    return normalizeClimate(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function exportClimateRiskPdf(id, filename) {
  try {
    const response = await axios.get(`${CLIMATE_PATH}/${id}/export_pdf`, {
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });
    await downloadHsePdfBlob(response.data, filename || `hse-klimarisiko-${id}.pdf`);
  } catch (error) {
    await raiseHsePdfRequestError(error);
  }
}

export async function fetchClimateRisks(filters = {}) {
  try {
    const params = listParams({
      ...filters,
      risk_category: filters.risk_category || filters.category,
    });
    const response = await axios.get(CLIMATE_PATH, { params });
    const { items, meta } = unwrapList(response.data);
    return { data: items.map(normalizeClimate), meta };
  } catch (error) {
    raiseRiskError(error);
  }
}

export async function createClimateRisk(form) {
  try {
    const response = await axios.post(CLIMATE_PATH, { climate_risk: toClimatePayload(form) });
    return normalizeClimate(unwrapRecord(response.data));
  } catch (error) {
    raiseRiskError(error);
  }
}
